import { useState, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  X as XIcon,
  Trophy,
  ChevronRight,
  Eye,
  EyeOff,
  Volume2,
  VolumeX,
  Maximize,
  RotateCcw,
  Home,
} from "lucide-react";

// --- tiny Web Audio helper (user-initiated, so autoplay policy is satisfied) ---
function useSounds(enabled) {
  const ctxRef = useRef(null);
  const ctx = () => {
    if (!enabled) return null;
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) ctxRef.current = new AC();
    }
    return ctxRef.current;
  };
  const tone = (freq, dur = 0.16, type = "sine", when = 0) => {
    const c = ctx();
    if (!c) return;
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = type;
    o.frequency.value = freq;
    o.connect(g);
    g.connect(c.destination);
    const t0 = c.currentTime + when;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.25, t0 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    o.start(t0);
    o.stop(t0 + dur + 0.02);
  };
  return {
    reveal: () => tone(660, 0.18, "triangle"),
    strike: () => { tone(160, 0.32, "sawtooth"); },
    win: () => { [523, 659, 784, 1046].forEach((f, i) => tone(f, 0.22, "triangle", i * 0.14)); },
  };
}

const boardStyles = `
.cd-tile { perspective: 1000px; }
.cd-tile-inner { position: relative; width: 100%; height: 100%; transform-style: preserve-3d; transition: transform 0.55s cubic-bezier(0.4,0,0.2,1); }
.cd-tile-inner.flipped { transform: rotateX(180deg); }
.cd-face { position: absolute; inset: 0; display: flex; align-items: center; -webkit-backface-visibility: hidden; backface-visibility: hidden; border-radius: 0.9rem; }
.cd-back { transform: rotateX(180deg); }
@keyframes cd-pop { 0% { transform: scale(0.6); opacity: 0; } 60% { transform: scale(1.08); } 100% { transform: scale(1); opacity: 1; } }
.cd-pop { animation: cd-pop 0.35s ease-out; }
@keyframes cd-strike { 0% { transform: scale(2.4); opacity: 0; } 50% { opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
.cd-strike-x { animation: cd-strike 0.4s ease-out; }
`;

export default function GameBoard({ game, canSave = false, onSaveMatch, onExit }) {
  const { t } = useTranslation();
  const questions = useMemo(() => game?.questions || [], [game]);

  const [started, setStarted] = useState(false);
  const [teamA, setTeamA] = useState("Equipo A");
  const [teamB, setTeamB] = useState("Equipo B");
  const [scoreA, setScoreA] = useState(0);
  const [scoreB, setScoreB] = useState(0);
  const [qi, setQi] = useState(0);
  const [revealed, setRevealed] = useState(() => new Set());
  const [strikes, setStrikes] = useState(0);
  const [strikeFlash, setStrikeFlash] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const [finished, setFinished] = useState(false);
  const [saved, setSaved] = useState(false);
  const sounds = useSounds(soundOn);

  const q = questions[qi] || { text: "", answers: [] };
  const roundPoints = useMemo(
    () => (q.answers || []).reduce((sum, a, i) => (revealed.has(i) ? sum + (a.points || 0) : sum), 0),
    [q, revealed],
  );

  useEffect(() => { setStrikeFlash(false); }, [qi]);

  const revealAnswer = (i) => {
    if (revealed.has(i)) return;
    setRevealed((s) => new Set(s).add(i));
    sounds.reveal();
  };

  const addStrike = () => {
    if (strikes >= 3) return;
    setStrikes((s) => s + 1);
    setStrikeFlash(true);
    sounds.strike();
    setTimeout(() => setStrikeFlash(false), 700);
  };

  const resetRound = () => { setRevealed(new Set()); setStrikes(0); };

  const award = (team) => {
    if (team === "A") setScoreA((s) => s + roundPoints);
    else setScoreB((s) => s + roundPoints);
    goNext();
  };

  const goNext = () => {
    if (qi + 1 >= questions.length) {
      setFinished(true);
      sounds.win();
      return;
    }
    setQi((i) => i + 1);
    setRevealed(new Set());
    setStrikes(0);
  };

  const endGame = () => { setFinished(true); sounds.win(); };

  const restart = () => {
    setScoreA(0); setScoreB(0); setQi(0); setRevealed(new Set());
    setStrikes(0); setFinished(false); setSaved(false); setStarted(false);
  };

  const goFullscreen = () => {
    const el = document.documentElement;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else el.requestFullscreen?.();
  };

  const winner = scoreA > scoreB ? teamA : scoreB > scoreA ? teamB : t("games.tie");

  const doSave = async () => {
    if (!onSaveMatch || saved) return;
    await onSaveMatch({
      game_id: game.id,
      game_title: game.title,
      team_a_name: teamA,
      team_b_name: teamB,
      team_a_score: scoreA,
      team_b_score: scoreB,
      questions_played: questions.length,
    });
    setSaved(true);
  };

  // ---------- PRE-GAME ----------
  if (!started) {
    return (
      <div className="min-h-screen bg-[#0A1329] text-white grid place-items-center px-6" data-testid="game-pregame">
        <style>{boardStyles}</style>
        <div className="w-full max-w-lg text-center">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FDE68A]">{t("games.brand_tagline")}</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold mt-3">{game.title}</h1>
          {game.description ? <p className="text-slate-300 mt-3">{game.description}</p> : null}
          <p className="text-slate-400 text-sm mt-2">{questions.length} {t("games.questions")}</p>
          <div className="mt-8 grid grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-wide text-[#3B82F6]">{t("games.team_a")}</label>
              <input
                data-testid="team-a-name"
                value={teamA}
                onChange={(e) => setTeamA(e.target.value)}
                className="w-full mt-1 bg-[#0F1E3D] border border-[#1E3A8A] rounded-xl px-3 py-2.5 text-center font-bold focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-[#F59E0B]">{t("games.team_b")}</label>
              <input
                data-testid="team-b-name"
                value={teamB}
                onChange={(e) => setTeamB(e.target.value)}
                className="w-full mt-1 bg-[#0F1E3D] border border-[#6B3E2E] rounded-xl px-3 py-2.5 text-center font-bold focus:outline-none focus:ring-2 focus:ring-[#F59E0B]"
              />
            </div>
          </div>
          <button
            data-testid="start-game-btn"
            disabled={questions.length === 0}
            onClick={() => setStarted(true)}
            className="mt-8 w-full bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-[#0A1329] font-extrabold text-lg py-3.5 rounded-2xl hover:brightness-110 transition-[filter] disabled:opacity-40"
          >
            {t("games.start_game")}
          </button>
          {onExit && (
            <button onClick={onExit} className="mt-3 text-slate-400 text-sm hover:text-white inline-flex items-center gap-1" data-testid="exit-pregame">
              <Home size={14} /> {t("common.back")}
            </button>
          )}
        </div>
      </div>
    );
  }

  // ---------- WINNER ----------
  if (finished) {
    return (
      <div className="min-h-screen bg-[#0A1329] text-white grid place-items-center px-6" data-testid="game-winner">
        <style>{boardStyles}</style>
        <div className="w-full max-w-lg text-center bg-gradient-to-b from-[#0F1E3D] to-[#0A1329] border-2 border-[#F59E0B] p-8 rounded-3xl shadow-[0_0_50px_rgba(245,158,11,0.3)] cd-pop">
          <Trophy size={56} className="mx-auto text-[#FDE68A]" />
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FDE68A] mt-4">{t("games.winner")}</div>
          <h1 className="font-display text-4xl font-extrabold mt-2" data-testid="winner-name">{winner}</h1>
          <div className="mt-6 grid grid-cols-2 gap-4">
            <div className="bg-[#0A1329] border border-[#1E3A8A] rounded-2xl p-4">
              <div className="text-xs text-[#3B82F6] font-semibold truncate">{teamA}</div>
              <div className="font-mono text-4xl font-black mt-1">{scoreA}</div>
            </div>
            <div className="bg-[#0A1329] border border-[#6B3E2E] rounded-2xl p-4">
              <div className="text-xs text-[#F59E0B] font-semibold truncate">{teamB}</div>
              <div className="font-mono text-4xl font-black mt-1">{scoreB}</div>
            </div>
          </div>
          <div className="mt-7 flex flex-col gap-2">
            {canSave && (
              <button
                onClick={doSave}
                disabled={saved}
                data-testid="save-match-btn"
                className="bg-[#10B981] text-white font-bold py-2.5 rounded-xl hover:brightness-110 transition-[filter] disabled:opacity-50"
              >
                {saved ? t("games.match_saved") : t("games.save_match")}
              </button>
            )}
            <button onClick={restart} data-testid="play-again-btn" className="bg-[#1E3A8A] text-white font-bold py-2.5 rounded-xl hover:brightness-110 transition-[filter] inline-flex items-center justify-center gap-2">
              <RotateCcw size={16} /> {t("games.play_again")}
            </button>
            {onExit && (
              <button onClick={onExit} className="text-slate-400 text-sm hover:text-white inline-flex items-center justify-center gap-1 mt-1" data-testid="exit-winner">
                <Home size={14} /> {t("games.back_to_games")}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---------- PLAY BOARD ----------
  return (
    <div className="min-h-screen bg-[#0A1329] text-white pb-28" data-testid="game-board">
      <style>{boardStyles}</style>

      {/* Strike flash */}
      {strikeFlash && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center pointer-events-none">
          <div className="flex gap-4">
            {Array.from({ length: strikes }).map((_, i) => (
              <XIcon key={i} size={120} strokeWidth={4} className="text-[#EF4444] cd-strike-x" />
            ))}
          </div>
        </div>
      )}

      {/* Top bar: scores + question counter */}
      <div className="max-w-6xl mx-auto px-4 pt-5">
        <div className="flex items-center justify-between gap-3">
          <TeamScore name={teamA} score={scoreA} tone="blue" testid="score-team-a" />
          <div className="text-center shrink-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400">{t("games.question")}</div>
            <div className="font-mono text-2xl font-bold" data-testid="question-counter">{qi + 1}/{questions.length}</div>
            <div className="flex items-center justify-center gap-1.5 mt-1">
              {[0, 1, 2].map((i) => (
                <span key={i} className={`w-6 h-6 rounded grid place-items-center ${i < strikes ? "bg-[#EF4444]" : "bg-white/10"}`}>
                  {i < strikes ? <XIcon size={14} strokeWidth={4} /> : null}
                </span>
              ))}
            </div>
          </div>
          <TeamScore name={teamB} score={scoreB} tone="amber" testid="score-team-b" />
        </div>

        {/* Question */}
        <div className="mt-6 bg-[#0F1E3D] border border-[#1E3A8A] rounded-2xl px-6 py-5 text-center">
          <p className="font-display text-xl sm:text-2xl font-bold" data-testid="game-question-text">{q.text}</p>
          <div className="text-[#FDE68A] text-sm mt-1 font-mono" data-testid="round-points">
            {t("games.round_points")}: {roundPoints}
          </div>
        </div>

        {/* Board tiles */}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(q.answers || []).map((a, i) => {
            const open = revealed.has(i);
            return (
              <button
                key={i}
                onClick={() => revealAnswer(i)}
                data-testid={`reveal-answer-tile-${i}`}
                className="cd-tile h-16 sm:h-[4.5rem] text-left"
              >
                <div className={`cd-tile-inner ${open ? "flipped" : ""}`}>
                  <div className="cd-face bg-gradient-to-br from-[#1E3A8A] to-[#0D1F47] border-2 border-[#3B82F6]/40 justify-center">
                    <span className="font-display text-2xl font-black text-white/80">{i + 1}</span>
                  </div>
                  <div className="cd-face cd-back bg-gradient-to-br from-[#F59E0B] via-[#D97706] to-[#B45309] border-2 border-[#FDE68A] justify-between px-4">
                    <span className="font-display font-bold text-[#0A1329] text-base sm:text-lg leading-tight">{a.text}</span>
                    <span className="font-mono font-black text-[#0A1329] text-2xl bg-white/30 rounded-lg px-2.5 py-0.5">{a.points}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Host answer key */}
        <div className="mt-5">
          <button
            onClick={() => setShowKey((v) => !v)}
            data-testid="toggle-answer-key"
            className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"
          >
            {showKey ? <EyeOff size={15} /> : <Eye size={15} />}
            {showKey ? t("games.hide_key") : t("games.show_key")}
          </button>
          {showKey && (
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="answer-key">
              {(q.answers || []).map((a, i) => (
                <button
                  key={i}
                  onClick={() => revealAnswer(i)}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm border ${revealed.has(i) ? "bg-[#10B981]/15 border-[#10B981]/40 text-[#A7F3D0]" : "bg-white/5 border-white/10 text-slate-200 hover:bg-white/10"}`}
                  data-testid={`answer-key-row-${i}`}
                >
                  <span className="truncate">{i + 1}. {a.text}</span>
                  <span className="font-mono font-bold ml-2">{a.points}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Host control bar */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] max-w-[95vw] bg-slate-900/90 border border-slate-700/80 backdrop-blur-xl px-3 sm:px-5 py-2.5 rounded-2xl flex items-center gap-2 sm:gap-3 shadow-2xl">
        <button onClick={addStrike} data-testid="add-strike-button" className="inline-flex items-center gap-1.5 bg-[#EF4444] text-white font-bold px-3 py-2 rounded-xl hover:brightness-110 transition-[filter]">
          <XIcon size={16} strokeWidth={3} /> <span className="hidden sm:inline">{t("games.strike")}</span>
        </button>
        <button onClick={resetRound} title={t("games.reset_round")} data-testid="reset-round-btn" className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors">
          <RotateCcw size={16} />
        </button>
        <div className="h-6 w-px bg-white/15" />
        <button onClick={() => award("A")} data-testid="award-team-a" className="bg-[#1E3A8A] text-white font-semibold text-sm px-3 py-2 rounded-xl hover:brightness-125 transition-[filter] max-w-[8rem] truncate">
          + {teamA}
        </button>
        <button onClick={() => award("B")} data-testid="award-team-b" className="bg-[#6B3E2E] text-white font-semibold text-sm px-3 py-2 rounded-xl hover:brightness-125 transition-[filter] max-w-[8rem] truncate">
          + {teamB}
        </button>
        <div className="h-6 w-px bg-white/15" />
        <button onClick={() => goNext()} data-testid="next-question-btn" className="inline-flex items-center gap-1 bg-white/10 hover:bg-white/20 transition-colors font-semibold text-sm px-3 py-2 rounded-xl">
          <span className="hidden sm:inline">{t("games.next")}</span> <ChevronRight size={16} />
        </button>
        <button onClick={endGame} data-testid="end-game-btn" className="inline-flex items-center gap-1 bg-[#F59E0B] text-[#0A1329] font-bold text-sm px-3 py-2 rounded-xl hover:brightness-110 transition-[filter]">
          <Trophy size={15} /> <span className="hidden sm:inline">{t("games.finish")}</span>
        </button>
        <div className="h-6 w-px bg-white/15 hidden sm:block" />
        <button onClick={() => setSoundOn((v) => !v)} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors hidden sm:inline-flex" data-testid="toggle-sound">
          {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
        </button>
        <button onClick={goFullscreen} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors hidden sm:inline-flex" data-testid="toggle-fullscreen">
          <Maximize size={16} />
        </button>
      </div>
    </div>
  );
}

function TeamScore({ name, score, tone, testid }) {
  const ring = tone === "blue" ? "border-[#1E3A8A] text-[#3B82F6]" : "border-[#6B3E2E] text-[#F59E0B]";
  return (
    <div className={`flex-1 max-w-[38%] bg-[#0F1E3D] border-2 rounded-2xl px-3 sm:px-5 py-3 ${ring}`} data-testid={testid}>
      <div className="text-[11px] sm:text-xs font-semibold uppercase tracking-wide truncate">{name}</div>
      <div className="font-mono text-4xl sm:text-5xl font-black text-white leading-none mt-1">{score}</div>
    </div>
  );
}
